import { useState } from 'react';
import { textureUrl } from '../api/catalog';
import { coloursOf, initialsOf, SIDE_COLOUR, sideOf } from './factionEmblems';

/**
 * A faction's flag: the game's own when the platform can reach it, and a drawn
 * one when it cannot.
 *
 * The game's flags are `.paa` textures inside the PBOs the panel already has on
 * its disk. This repository ships nobody else's art and never will; what it does
 * is serve what the host it runs beside already owns
 * (`/api/catalog/texture`), and fall back to what it drew before — two bands in
 * the faction's own colours, the side's colour as the staff and the border, and
 * the faction's initials — whenever that fails. It fails often and on purpose:
 * a mod that spells its path differently, an encrypted PBO, a catalogue scanned
 * before the probe learnt to read a flag at all.
 *
 * It will also fail for most vanilla factions, and that is not a defect. A
 * dedicated server never renders, so SteamCMD ships it stripped base-game
 * textures — a valid PAA holding an 8x8 grey square — and the platform refuses
 * anything that small rather than publishing it as a flag. Mod factions carry
 * their real ones. The panel found this out the hard way and published a grey
 * square before it did.
 *
 * Decorative either way: the faction's name is always beside it, so it carries
 * no alternative text of its own.
 */
export function FactionEmblem({
  name,
  factionClass,
  side,
  size = 44,
  picture,
}: {
  name: string;
  factionClass: string;
  side?: string | null;
  size?: number;
  /** The faction's own flag, as a texture path in the game's files. */
  picture?: string | null;
}) {
  const [broken, setBroken] = useState(false);
  if (picture && !broken) {
    return (
      <img
        className="faction-emblem"
        src={textureUrl(picture)}
        width={size}
        height={size * 0.68}
        alt=""
        aria-hidden="true"
        onError={() => setBroken(true)}
      />
    );
  }
  const [dark, light] = coloursOf(factionClass);
  const edge = SIDE_COLOUR[sideOf(side)];
  const id = `f${factionClass.replace(/[^A-Za-z0-9]/g, '')}`;
  return (
    <svg
      className="faction-emblem"
      width={size}
      height={size * 0.68}
      viewBox="0 0 100 68"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <clipPath id={`${id}-clip`}>
          <rect x="8" y="4" width="88" height="60" rx="3" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${id}-clip)`}>
        <rect x="8" y="4" width="88" height="60" fill={dark} />
        <path d="M8 44 L96 20 L96 64 L8 64 Z" fill={light} />
        <circle cx="30" cy="24" r="9" fill={edge} opacity="0.85" />
      </g>
      <rect x="8" y="4" width="88" height="60" rx="3" fill="none" stroke={edge} strokeWidth="3" />
      {/* The staff, which is what makes it read as a flag and not as a swatch. */}
      <rect x="2" y="2" width="4" height="64" rx="2" fill={edge} />
      <text
        x="60"
        y="46"
        textAnchor="middle"
        fontSize="26"
        fontWeight="700"
        fill="#f2f4f7"
        opacity="0.92"
      >
        {initialsOf(name)}
      </text>
    </svg>
  );
}
